// React
import React, { useCallback, useEffect, useRef, useState } from 'react';

// Assets
import { CloseOutlined } from '@ant-design/icons';

// Components
import { ConsumeOtcModal, PropertyNotesModal } from 'components';
import { PopupTransition } from 'components/@extended/Transitions';

// Firebase
import { db, fsTimeStampNow, fsTools, timeStampNowSeconds } from 'config/firebase';
import { getPropertyAccessLogs } from '../../store/sagas/AccessLogs';

// Packages
import { PatternFormat } from 'react-number-format';
import {
    Stack,
    Typography,
    List,
    ListItem,
    ListItemText,
    Tabs,
    Tab,
    Button,
    IconButton,
    Box,
    Dialog,
    OutlinedInput
} from '@mui/material';

// Utils
import { propertyTypes, userRoles, userRolesNames } from 'utils/constants';
import { Loader } from '../index';
import { useLogType } from 'utils/helpers/useLogType';
import { AccessLogHelper } from '../../utils/helpers/access-log.helper';
import { GuestHelper } from '../../utils/helpers/guest.helper';
import {
    formatAccessWindow,
    formatCodeWindow
} from '../../utils/helpers/access-window.helper';
import { getTimestampSeconds } from '../../utils/helpers/access-status.helper';
import { openModal } from '../../utils/modal/modal.helper';
import { isStaffEmail } from '../../utils/helpers/email.helper';

const { collection, onSnapshot, query, where } = fsTools;

const GUEST_LIST_ROLES = [userRoles.GUEST, userRoles.ASSOCIATE];

const OPERATOR_TYPE_LABELS = {
    manager: 'Manager',
    virtual_guard: 'Virtual Guard',
    guard: 'Guard',
    employee: 'Employee',
    supervisor: 'Supervisor',
    rental_manager: 'Rental Manager'
};

const PropertyDetails = ({
    members,
    orgId,
    propertyId,
    propertyAddress,
    getMemberKeys,
    resetPropertyMembers,
    setPropertyAddress,
    setMember,
    setConsumerId,
    setDrawerType,
    setDrawerTitle,
    visitors,
    setVisitors,
    setTempVisitor,
    membersCodes,
    panels,
    activePanel,
    propertyNotes,
    propertyType,
    openGuardLog
}) => {
    const [activeTab, setActiveTab] = useState(0);
    const [activeTopTab, setActiveTopTab] = useState(0);
    const [accessLogs, setAccessLogs] = useState([]);
    const [accessLogsLastDoc, setAccessLogsLastDoc] = useState(null);
    const [accessLogsHasMore, setAccessLogsHasMore] = useState(false);
    const [accessLogsLoadingMore, setAccessLogsLoadingMore] = useState(false);
    const [openPropertyNotesModal, setOpenPropertyNotesModal] = useState(false);
    const [propertyAccessKeys, setPropertyAccessKeys] = useState([]);
    const getLogType = useLogType();
    const [loading, setLoading] = useState(false);
    const [occupantSearch, setOccupantSearch] = useState('');
    const [guestSearch, setGuestSearch] = useState('');
    const [operatorSearch, setOperatorSearch] = useState('');

    const occupantMembers = members.filter(
        member =>
            (member?.invited_at && member?.role === 'resi') ||
            (member?.properties && member?.properties[propertyId]?.role === 'resi')
    );

    const occupants = occupantMembers.map(occupant => {
        return {
            ...occupant,
            status: occupant.invited_at
                ? 'Invited'
                : occupant?.super_suspend || occupant?.properties?.[propertyId]?.suspended
                ? 'Suspended'
                : 'Active'
        };
    });

    const tenantMembers = members.filter(
        member =>
            (member?.invited_at && member?.role === 'tenant') ||
            (member?.properties && member?.properties[propertyId]?.role === 'tenant')
    );

    const tenants = tenantMembers.map(tenant => {
        return {
            ...tenant,
            status: tenant.invited_at
                ? 'Invited'
                : tenant?.super_suspend || tenant?.properties?.[propertyId]?.suspended
                ? 'Suspended'
                : 'Active'
        };
    });

    // Sage staff (ramco.io, ramcoprotective.com) are not shown on the org property
    const operatorMembers = members.filter(
        member =>
            ((member?.invited_at && member?.role === 'operator') ||
                (member?.properties &&
                    member?.properties[propertyId]?.role === 'operator')) &&
            !(propertyType === propertyTypes.ORGANIZATION && isStaffEmail(member?.email))
    );

    // This patch below comprised of 'groupedByphone' and 'operators' will be removed
    // once API handles invites for operators/team members like all other user
    // invites within Sage.  Just a temporary fix to avoid duplicate operator
    // items and throwing off total operator count for the org property

    const groupedByPhone = operatorMembers.reduce((acc, occupant) => {
        const phone = occupant.phone_number || `__no_phone__${occupant.uid}`;

        if (!acc[phone]) {
            acc[phone] = [];
        }

        acc[phone].push(occupant);
        return acc;
    }, {});

    const operators = Object.values(groupedByPhone).flatMap(group => {
        const invited = group.filter(member => member.invited_at);
        const nonInvited = group.filter(member => !member.invited_at);

        if (group.length === 1) {
            const occupant = group[0];

            return [
                {
                    ...occupant,
                    status: occupant.invited_at
                        ? 'Invited'
                        : occupant?.super_suspend ||
                          occupant?.properties?.[propertyId]?.suspended
                        ? 'Suspended'
                        : 'Active'
                }
            ];
        }

        if (invited.length === 1 && nonInvited.length === 1) {
            return [
                {
                    ...nonInvited[0],
                    status: 'Invited'
                }
            ];
        }

        return group.map(occupant => ({
            ...occupant,
            status: occupant.invited_at
                ? 'Invited'
                : occupant?.super_suspend || occupant?.properties?.[propertyId]?.suspended
                ? 'Suspended'
                : 'Active'
        }));
    });

    // Do not remove commented 'operators' function below.  Will remove ugly patch function above once API handles operator invites like all other invites
    // instead of the patch that they are doing instead since it was the quickest way to properly handle operator
    // invites in the short time frame given.  The function below will resume once API is rectified

    // const operators = operatorMembers.map(occupant => {
    //     return {
    //         ...occupant,
    //         status: occupant.invited_at
    //             ? 'Invited'
    //             : occupant?.super_suspend
    //             ? 'Suspended'
    //             : 'Active'
    //     };
    // });

    const guestMembers = members.filter(
        member =>
            (member?.invited_at && GUEST_LIST_ROLES.includes(member?.role)) ||
            (member?.properties &&
                GUEST_LIST_ROLES.includes(member?.properties[propertyId]?.role))
    );

    const guests = guestMembers.map(guest => {
        return {
            ...guest,
            status: guest.invited_at
                ? 'Invited'
                : guest?.super_suspend || guest?.properties?.[propertyId]?.suspended
                ? 'Suspended'
                : 'Active'
        };
    });

    const isMinor = member => {
        if (member?.status === 'Invited') {
            return member?.minor === true;
        }

        return member?.properties?.[propertyId]?.minor === true;
    };

    const sortByName = (a, b) => {
        const nameA = `${a?.last_name ?? ''} ${a?.first_name ?? ''}`.trim().toLowerCase();
        const nameB = `${b?.last_name ?? ''} ${b?.first_name ?? ''}`.trim().toLowerCase();
        return nameA.localeCompare(nameB);
    };

    const sortedOccupants = [...occupants].sort(sortByName);
    const sortedTenants = [...tenants].sort(sortByName);

    const filteredOccupants = occupantSearch.trim()
        ? sortedOccupants.filter(o => {
              const term = occupantSearch.trim().toLowerCase();
              const firstLast = `${o?.first_name ?? ''} ${o?.last_name ?? ''}`
                  .trim()
                  .toLowerCase();
              const lastFirst = `${o?.last_name ?? ''} ${o?.first_name ?? ''}`
                  .trim()
                  .toLowerCase();
              return firstLast.includes(term) || lastFirst.includes(term);
          })
        : sortedOccupants;

    const filteredTenants = occupantSearch.trim()
        ? sortedTenants.filter(t => {
              const term = occupantSearch.trim().toLowerCase();
              const firstLast = `${t?.first_name ?? ''} ${t?.last_name ?? ''}`
                  .trim()
                  .toLowerCase();
              const lastFirst = `${t?.last_name ?? ''} ${t?.first_name ?? ''}`
                  .trim()
                  .toLowerCase();
              return firstLast.includes(term) || lastFirst.includes(term);
          })
        : sortedTenants;

    const allGuests = [
        ...guests.map(g => ({ ...g, _type: 'guest' })),
        ...(visitors ?? []).map(v => ({ ...v, _type: 'visitor' }))
    ].sort(sortByName);

    const filteredOperators = operatorSearch.trim()
        ? operators.filter(o => {
              const term = operatorSearch.trim().toLowerCase();
              const firstLast = `${o?.first_name ?? ''} ${o?.last_name ?? ''}`
                  .trim()
                  .toLowerCase();
              const lastFirst = `${o?.last_name ?? ''} ${o?.first_name ?? ''}`
                  .trim()
                  .toLowerCase();
              return firstLast.includes(term) || lastFirst.includes(term);
          })
        : operators;

    const filteredGuests = guestSearch.trim()
        ? allGuests.filter(item => {
              const term = guestSearch.trim().toLowerCase();
              const firstLast = `${item?.first_name ?? ''} ${item?.last_name ?? ''}`
                  .trim()
                  .toLowerCase();
              const lastFirst = `${item?.last_name ?? ''} ${item?.first_name ?? ''}`
                  .trim()
                  .toLowerCase();
              const formattedName = GuestHelper.formatName(item).toLowerCase();
              return (
                  firstLast.includes(term) ||
                  lastFirst.includes(term) ||
                  formattedName.includes(term)
              );
          })
        : allGuests;

    // Auto-open the notes once per property. Keying off the notes array alone reopened
    // the modal on every property refetch, since each fetch returns a new reference.
    const notesShownForPropertyRef = useRef(null);

    useEffect(() => {
        if (!propertyId) {
            notesShownForPropertyRef.current = null;
            return;
        }

        if (notesShownForPropertyRef.current === propertyId) return;
        if (!propertyNotes || !propertyNotes.length) return;

        notesShownForPropertyRef.current = propertyId;
        setOpenPropertyNotesModal(true);
    }, [propertyId, propertyNotes, setOpenPropertyNotesModal]);

    useEffect(() => {
        if (!propertyId) {
            setPropertyAccessKeys([]);
            return undefined;
        }

        const accessKeysQuery = query(
            collection(db, 'access_keys'),
            where('property_id', '==', propertyId)
        );

        const unsubscribe = onSnapshot(
            accessKeysQuery,
            querySnapshot => {
                const keys = [];
                querySnapshot.forEach(doc => {
                    keys.push(doc.data());
                });
                setPropertyAccessKeys(keys);
            },
            error => {
                console.error('Error watching property access keys:', error);
                setPropertyAccessKeys([]);
            }
        );

        return unsubscribe;
    }, [propertyId]);

    const getGuestKey = guest =>
        propertyAccessKeys.find(key => {
            const keyConsumerId = key?.consumer_id;
            const keyId = key?.key_id;

            return (
                (keyConsumerId &&
                    (keyConsumerId === guest?.uid ||
                        keyConsumerId === guest?.consumer_id)) ||
                (keyId && keyId === guest?.key_id)
            );
        });

    const getGuestCompanyName = guest => getGuestKey(guest)?.company_name?.trim() || '';

    const getGuestAccessWindow = guest => {
        const schedule = guest?.invited_at ? guest : getGuestKey(guest);
        return schedule ? formatAccessWindow(schedule) : null;
    };

    const listFloor = count => (count ? `${Math.min(count, 2) * 48}px` : '4vh');

    const getPropertyEntry = member =>
        member?.invited_at ? member : member?.properties?.[propertyId];

    const getOccupantLabel = member =>
        getPropertyEntry(member)?.primary === true ? 'Primary' : 'Occupant';

    const getOperatorLabel = operator =>
        OPERATOR_TYPE_LABELS[
            getPropertyEntry(operator)?.type || operator?.orgs?.[orgId]?.type
        ] || null;

    const renderSubtext = subtext =>
        subtext && (
            <Typography
                sx={{
                    fontSize: '11.5px',
                    lineHeight: 1.3,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block'
                }}
                component="span"
                variant="body2"
                color="text.secondary"
                title={subtext}
            >
                {subtext}
            </Typography>
        );

    const getGuestListDisplay = guest => {
        const name = GuestHelper.formatName(guest);
        const companyName = getGuestCompanyName(guest);

        return companyName ? `${name} - ${companyName}` : name;
    };

    const getCodeOwner = code => {
        const filteredMembers = members.filter(member => member.uid === code.creator_id);
        if (filteredMembers.length)
            return `${filteredMembers[0].first_name} ${filteredMembers[0].last_name}`;
        return 'n/a';
    };

    const codes = membersCodes
        ? membersCodes.map(code => ({ ...code, createdBy: getCodeOwner(code) }))
        : [];

    const filteredCodes = codes.filter(
        code => code.expires_at.seconds > timeStampNowSeconds()
    );

    const sortedCodes = filteredCodes.sort((a, b) => {
        const nameA = a.createdBy.toLowerCase();
        const nameB = b.createdBy.toLowerCase();
        return nameA.localeCompare(nameB);
    });

    const handleChange = tab => {
        if (tab.includes('1')) {
            setActiveTab(1);
        } else if (tab.includes('2')) {
            setActiveTab(2);
        } else {
            setActiveTab(0);
        }
    };

    const handleTopTabChange = (event, newValue) => {
        setActiveTopTab(newValue);
    };

    const handleMemberDetails = member => {
        setMember(member);

        const fullName = `${member?.first_name ?? ''} ${member?.last_name ?? ''}`.trim();
        setDrawerTitle(fullName || member?.company_name || '');

        if (!member.invited_at) {
            setConsumerId(member.uid);
            getMemberKeys({ propertyId, memberId: member.uid });
        }

        openGuardLog({ member });
    };

    const handleLogNewVisitor = () => {
        setTempVisitor(null);
        openGuardLog();
    };

    const handleLogCurrentVisitor = visitor => {
        openGuardLog({ visitor });
    };

    const isActive = code => {
        const codeStartSec =
            getTimestampSeconds(code?.starts_at) ?? getTimestampSeconds(code?.created_at);
        const codeEndSec = code?.expires_at?.seconds;
        const nowSec = fsTimeStampNow().seconds;
        if (nowSec >= codeStartSec && nowSec < codeEndSec) return true;
        return false;
    };

    const handleOpenOtcModal = otc => {
        openModal(ConsumeOtcModal, {
            otc: otc,
            panel: activePanel,
            kiosks: panels?.[orgId] || []
        });
    };

    const TabIcon = ({ number }) => (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '25px',
                width: '25px',
                backgroundColor: 'rgba(45, 143, 87, .1)',
                borderRadius: '5px',
                marginLeft: '5px'
            }}
        >
            <Typography
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontSize: '13px',
                    fontWeight: '500'
                }}
                component="span"
                variant="body1"
                color="rgba(45, 143, 87, 1)"
            >
                {number}
            </Typography>
        </div>
    );

    useEffect(() => {
        async function fetchPropertyAccessLogs() {
            try {
                setLoading(true);
                const { logs, lastDoc, hasMore } = await getPropertyAccessLogs(
                    orgId,
                    propertyId
                );
                setAccessLogs(logs);
                setAccessLogsLastDoc(lastDoc);
                setAccessLogsHasMore(hasMore);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }

        if (propertyId) {
            setAccessLogs([]);
            setAccessLogsLastDoc(null);
            setAccessLogsHasMore(false);
            setOccupantSearch('');
            setGuestSearch('');
            setOperatorSearch('');
            fetchPropertyAccessLogs();
        }
    }, [propertyId, orgId]);

    const handleLoadMoreAccessLogs = useCallback(async () => {
        if (!accessLogsLastDoc || accessLogsLoadingMore) return;
        try {
            setAccessLogsLoadingMore(true);
            const {
                logs: next,
                lastDoc,
                hasMore
            } = await getPropertyAccessLogs(orgId, propertyId, accessLogsLastDoc);
            setAccessLogs(prev => {
                const seen = new Set(prev.map(l => l.id));
                return [...prev, ...next.filter(l => !seen.has(l.id))];
            });
            setAccessLogsLastDoc(lastDoc);
            setAccessLogsHasMore(hasMore);
        } catch (err) {
            console.error(err);
        } finally {
            setAccessLogsLoadingMore(false);
        }
    }, [orgId, propertyId, accessLogsLastDoc, accessLogsLoadingMore]);

    return (
        <>
            {loading && <Loader />}
            <ConsumeOtcModal />
            <Stack
                sx={{
                    height: '77vh',
                    marginTop: '2vh',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Stack
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1.5vh',
                        height: '1.8vh',
                        width: '100%',
                        flexShrink: 0
                    }}
                >
                    <Typography
                        sx={{
                            display: 'block',
                            fontSize: '16px',
                            fontWeight: '500',
                            textTransform: 'capitalize'
                        }}
                        component="span"
                        variant="body1"
                    >
                        {propertyAddress}&nbsp;&nbsp;
                        {propertyType === propertyTypes.RENTAL && (
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    borderRadius: '5px',
                                    paddingX: 1
                                }}
                                component="span"
                                variant="body1"
                                color="success.main"
                                bgcolor="success.lighter"
                            >
                                Rental
                            </Typography>
                        )}
                    </Typography>
                    <IconButton
                        onClick={() => {
                            setVisitors(null);
                            setPropertyAddress('');
                            resetPropertyMembers();
                        }}
                    >
                        <CloseOutlined style={{ width: '16px' }} />
                    </IconButton>
                </Stack>
                {propertyNotes && propertyNotes.length ? (
                    <Stack
                        sx={{
                            display: 'flex',
                            flex: '1 1 0',
                            minHeight: 'calc(8.4vh + 2px)',
                            maxHeight: 'max-content',
                            marginBottom: '2vh',
                            border: '1px solid rgba(6, 211, 134, .7)'
                        }}
                    >
                        <Stack
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                height: '4.4vh',
                                width: '100%',
                                flexShrink: 0,
                                backgroundColor: 'rgba(6, 211, 134, .7)'
                            }}
                        >
                            <Typography
                                sx={{
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    paddingLeft: '3%'
                                }}
                                component="span"
                                variant="body1"
                                color="text.primary"
                            >
                                Notes
                            </Typography>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '24px',
                                    width: '24px',
                                    backgroundColor: 'rgba(243,243,243,1)',
                                    borderRadius: '5px',
                                    marginLeft: '5px'
                                }}
                            >
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: '13px',
                                        fontWeight: '500'
                                    }}
                                    component="span"
                                    variant="body1"
                                    color="rgba(109, 109, 109, 1)"
                                >
                                    {propertyNotes.length}
                                </Typography>
                            </div>
                        </Stack>
                        <List
                            sx={{
                                width: '100%',
                                overflowY: 'auto',
                                flex: '0 1 auto',
                                minHeight: '4vh',
                                maxHeight: '12vh',
                                borderLeft: '1px solid rgba(243, 243, 243, 1)',
                                borderRight: '1px solid rgba(243, 243, 243, 1)',
                                borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                padding: 0,
                                margin: 0
                            }}
                        >
                            {propertyNotes.map(note => (
                                <ListItem
                                    key={note.content}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        height: '4vh',
                                        width: '100%',
                                        backgroundColor: '#ffffff',
                                        borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                        paddingLeft: '3%'
                                    }}
                                    disableGutters
                                >
                                    <ListItemText
                                        sx={{
                                            maxWidth: '40%!important',
                                            left: '4%'
                                        }}
                                        primary={
                                            <Typography
                                                sx={{
                                                    fontSize: '13px',
                                                    textTransform: 'capitalize'
                                                }}
                                                component="span"
                                                variant="body1"
                                                color="text.primary"
                                                title={note.content}
                                            >
                                                {`- ${note.content}`}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Stack>
                ) : null}
                <Stack
                    sx={{
                        display: 'contents'
                    }}
                >
                    <Stack
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            height: '5.4vh',
                            width: '100%',
                            flexShrink: 0,
                            border: '1px solid rgba(0, 0, 0, 0.06)'
                        }}
                    >
                        {propertyType && propertyType === 'org' ? (
                            <Tabs
                                value={activeTopTab}
                                onChange={handleTopTabChange}
                                indicatorColor="transparent"
                            >
                                <Tab
                                    icon={<TabIcon number={operators.length} />}
                                    iconPosition="end"
                                    sx={{ fontSize: '13px' }}
                                    label="Operators"
                                    id="top-tab-0"
                                />
                            </Tabs>
                        ) : (
                            <Tabs
                                value={activeTopTab}
                                onChange={handleTopTabChange}
                                indicatorColor="transparent"
                            >
                                <Tab
                                    icon={<TabIcon number={occupants.length} />}
                                    iconPosition="end"
                                    sx={{ fontSize: '13px' }}
                                    label="Occupants"
                                    id="top-tab-0"
                                />

                                {tenants && tenants.length > 0 && (
                                    <Tab
                                        icon={<TabIcon number={tenants.length} />}
                                        iconPosition="end"
                                        sx={{ fontSize: '13px' }}
                                        label="Tenants"
                                        id="top-tab-1"
                                    />
                                )}
                            </Tabs>
                        )}
                    </Stack>

                    {propertyType === 'org' ? (
                        <div
                            role="tabpanel"
                            id="tabpanel-operators"
                            aria-labelledby="top-tab-0"
                            style={{ display: 'contents' }}
                        >
                            <OutlinedInput
                                type="search"
                                placeholder="Search operators…"
                                value={operatorSearch}
                                onChange={e => setOperatorSearch(e.target.value)}
                                size="small"
                                inputProps={{
                                    sx: {
                                        fontSize: '13px',
                                        height: '1.8vh',
                                        backgroundColor: 'rgba(249, 249, 250, 1)'
                                    }
                                }}
                                sx={{ width: '100%', my: '6px', flexShrink: 0 }}
                            />
                            <List
                                sx={{
                                    width: '100%',
                                    overflowY: 'auto',
                                    flex: '2 1 0',
                                    minHeight: listFloor(filteredOperators.length),
                                    maxHeight: 'max-content',
                                    borderLeft: '1px solid rgba(243, 243, 243, 1)',
                                    borderRight: '1px solid rgba(243, 243, 243, 1)',
                                    borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                    padding: 0,
                                    margin: 0
                                }}
                            >
                                {filteredOperators.map(operator => (
                                    <ListItem
                                        key={operator.uid || operator.invite_id}
                                        button
                                        disableGutters
                                        onClick={() => handleMemberDetails(operator)}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            minHeight: '48px',
                                            paddingY: '6px',
                                            width: '100%',
                                            backgroundColor: '#ffffff',
                                            borderBottom:
                                                '1px solid rgba(243, 243, 243, 1)',
                                            paddingLeft: '3%'
                                        }}
                                    >
                                        <>
                                            <ListItemText
                                                sx={{
                                                    maxWidth: '40%!important',
                                                    left: '4%',
                                                    my: 0
                                                }}
                                                primary={
                                                    <Typography
                                                        sx={{
                                                            fontSize: '13px',
                                                            textTransform: 'capitalize'
                                                        }}
                                                        component="span"
                                                        variant="body1"
                                                        color="text.primary"
                                                    >
                                                        {`${operator?.last_name} ${operator?.first_name}`}
                                                    </Typography>
                                                }
                                                secondary={renderSubtext(
                                                    getOperatorLabel(operator)
                                                )}
                                            />
                                            <ListItemText
                                                sx={{
                                                    maxWidth: '40%!important'
                                                }}
                                                primary={
                                                    <Typography
                                                        sx={{ fontSize: '13px' }}
                                                        component="span"
                                                        variant="body1"
                                                        color="text.primary"
                                                    >
                                                        <PatternFormat
                                                            value={
                                                                operator?.phone?.number
                                                                    ? operator.phone
                                                                          .number
                                                                    : ''
                                                            }
                                                            displayType={'text'}
                                                            format={`+${operator.phone?.code} (###)-###-####`}
                                                        />
                                                    </Typography>
                                                }
                                            />
                                            {operator.status !== 'Active' && (
                                                <ListItemText
                                                    sx={{
                                                        display: 'flex',
                                                        position: 'absolute',
                                                        right: '3%',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        width: '15%'
                                                    }}
                                                    primary={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                height: '80%',

                                                                backgroundColor:
                                                                    operator.status ===
                                                                    'Active'
                                                                        ? 'rgba(45, 143, 87, .1)'
                                                                        : operator.status ===
                                                                          'Invited'
                                                                        ? 'rgba(255, 192, 0, .1)'
                                                                        : 'rgba(255, 0, 0, .1)',
                                                                borderRadius: '5px'
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    marginX: '10px'
                                                                }}
                                                                component="span"
                                                                variant="body1"
                                                                color={
                                                                    operator.status ===
                                                                    'Active'
                                                                        ? 'rgba(45, 143, 87, 1)'
                                                                        : operator.status ===
                                                                          'Invited'
                                                                        ? 'rgba(255, 192, 0, 1)'
                                                                        : 'rgba(255, 0, 0, 1)'
                                                                }
                                                            >
                                                                {`${operator?.status}`}
                                                            </Typography>
                                                        </div>
                                                    }
                                                />
                                            )}
                                        </>
                                    </ListItem>
                                ))}
                                {(!filteredOperators ||
                                    filteredOperators.length === 0) && (
                                    <div
                                        style={{
                                            marginLeft: '3%',
                                            height: '4vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '13px'
                                        }}
                                    >
                                        Operators not found.
                                    </div>
                                )}
                            </List>
                        </div>
                    ) : (
                        <>
                            <OutlinedInput
                                type="search"
                                placeholder="Search occupants…"
                                value={occupantSearch}
                                onChange={e => setOccupantSearch(e.target.value)}
                                size="small"
                                inputProps={{
                                    sx: {
                                        fontSize: '13px',
                                        height: '1.8vh',
                                        backgroundColor: 'rgba(249, 249, 250, 1)'
                                    }
                                }}
                                sx={{ width: '100%', my: '6px', flexShrink: 0 }}
                            />
                            <div
                                role="tabpanel"
                                hidden={activeTopTab !== 0}
                                id="tabpanel-occupants"
                                aria-labelledby="top-tab-0"
                                style={{
                                    display: activeTopTab === 0 ? 'contents' : 'none'
                                }}
                            >
                                <List
                                    sx={{
                                        width: '100%',
                                        overflowY: 'auto',
                                        flex: '2 1 0',
                                        minHeight: listFloor(filteredOccupants.length),
                                        maxHeight: 'max-content',
                                        borderLeft: '1px solid rgba(243, 243, 243, 1)',
                                        borderRight: '1px solid rgba(243, 243, 243, 1)',
                                        borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                        padding: 0,
                                        margin: 0
                                    }}
                                >
                                    {filteredOccupants.map(occupant => (
                                        <ListItem
                                            key={occupant.uid || occupant.invite_id}
                                            button
                                            disableGutters
                                            onClick={() => handleMemberDetails(occupant)}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                minHeight: '48px',
                                                paddingY: '6px',
                                                width: '100%',
                                                backgroundColor: '#ffffff',
                                                borderBottom:
                                                    '1px solid rgba(243, 243, 243, 1)',
                                                paddingLeft: '3%'
                                            }}
                                        >
                                            <>
                                                <ListItemText
                                                    sx={{
                                                        maxWidth: '40%!important',
                                                        left: '4%',
                                                        my: 0
                                                    }}
                                                    primary={
                                                        <Typography
                                                            sx={{
                                                                fontSize: '13px',
                                                                textTransform:
                                                                    'capitalize'
                                                            }}
                                                            component="span"
                                                            variant="body1"
                                                            color="text.primary"
                                                        >
                                                            {`${occupant?.last_name} ${occupant?.first_name}`}
                                                        </Typography>
                                                    }
                                                    secondary={renderSubtext(
                                                        getOccupantLabel(occupant)
                                                    )}
                                                />
                                                <ListItemText
                                                    sx={{
                                                        maxWidth: '40%!important'
                                                    }}
                                                    primary={
                                                        <Typography
                                                            sx={{ fontSize: '13px' }}
                                                            component="span"
                                                            variant="body1"
                                                            color="text.primary"
                                                        >
                                                            <PatternFormat
                                                                value={
                                                                    occupant?.phone
                                                                        ?.number
                                                                        ? occupant.phone
                                                                              .number
                                                                        : ''
                                                                }
                                                                displayType={'text'}
                                                                format={`+${occupant.phone?.code} (###)-###-####`}
                                                            />
                                                        </Typography>
                                                    }
                                                />
                                                {isMinor(occupant) && (
                                                    <ListItemText
                                                        sx={{
                                                            display: 'flex',
                                                            position: 'absolute',
                                                            right:
                                                                occupant.status ===
                                                                    'Invited' ||
                                                                occupant.status ===
                                                                    'Suspended'
                                                                    ? '19%'
                                                                    : '3%',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '12%'
                                                        }}
                                                        primary={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent:
                                                                        'center',
                                                                    alignItems: 'center',
                                                                    height: '80%',
                                                                    backgroundColor:
                                                                        'rgba(255, 0, 0, .1)',
                                                                    borderRadius: '5px'
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '13px',
                                                                        marginX: '10px'
                                                                    }}
                                                                    component="span"
                                                                    variant="body1"
                                                                    color="rgba(255, 0, 0, 1)"
                                                                >
                                                                    Minor
                                                                </Typography>
                                                            </div>
                                                        }
                                                    />
                                                )}
                                                {occupant.status !== 'Active' && (
                                                    <ListItemText
                                                        sx={{
                                                            display: 'flex',
                                                            position: 'absolute',
                                                            right: '3%',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '15%'
                                                        }}
                                                        primary={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent:
                                                                        'center',
                                                                    alignItems: 'center',
                                                                    height: '80%',

                                                                    backgroundColor:
                                                                        occupant.status ===
                                                                        'Active'
                                                                            ? 'rgba(45, 143, 87, .1)'
                                                                            : occupant.status ===
                                                                              'Invited'
                                                                            ? 'rgba(255, 192, 0, .1)'
                                                                            : 'rgba(255, 0, 0, .1)',
                                                                    borderRadius: '5px'
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '13px',
                                                                        marginX: '10px'
                                                                    }}
                                                                    component="span"
                                                                    variant="body1"
                                                                    color={
                                                                        occupant.status ===
                                                                        'Active'
                                                                            ? 'rgba(45, 143, 87, 1)'
                                                                            : occupant.status ===
                                                                              'Invited'
                                                                            ? 'rgba(255, 192, 0, 1)'
                                                                            : 'rgba(255, 0, 0, 1)'
                                                                    }
                                                                >
                                                                    {`${occupant?.status}`}
                                                                </Typography>
                                                            </div>
                                                        }
                                                    />
                                                )}
                                            </>
                                        </ListItem>
                                    ))}
                                    {(!filteredOccupants ||
                                        filteredOccupants.length === 0) && (
                                        <div
                                            style={{
                                                marginLeft: '3%',
                                                height: '4vh',
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Occupants not found.
                                        </div>
                                    )}
                                </List>
                            </div>

                            <div
                                role="tabpanel"
                                hidden={activeTopTab !== 1}
                                id="tabpanel-tenants"
                                aria-labelledby="top-tab-1"
                                style={{
                                    display: activeTopTab === 1 ? 'contents' : 'none'
                                }}
                            >
                                <List
                                    sx={{
                                        width: '100%',
                                        overflowY: 'auto',
                                        flex: '2 1 0',
                                        minHeight: listFloor(filteredTenants.length),
                                        maxHeight: 'max-content',
                                        borderLeft: '1px solid rgba(243, 243, 243, 1)',
                                        borderRight: '1px solid rgba(243, 243, 243, 1)',
                                        borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                        padding: 0,
                                        margin: 0
                                    }}
                                >
                                    {filteredTenants.map(tenant => (
                                        <ListItem
                                            key={tenant.uid || tenant.invite_id}
                                            button
                                            disableGutters
                                            onClick={() => handleMemberDetails(tenant)}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                minHeight: '48px',
                                                paddingY: '6px',
                                                width: '100%',
                                                backgroundColor: '#ffffff',
                                                borderBottom:
                                                    '1px solid rgba(243, 243, 243, 1)',
                                                paddingLeft: '3%'
                                            }}
                                        >
                                            <>
                                                <ListItemText
                                                    sx={{
                                                        maxWidth: '40%!important',
                                                        left: '4%',
                                                        my: 0
                                                    }}
                                                    primary={
                                                        <Typography
                                                            sx={{
                                                                fontSize: '13px',
                                                                textTransform:
                                                                    'capitalize'
                                                            }}
                                                            component="span"
                                                            variant="body1"
                                                            color="text.primary"
                                                        >
                                                            {`${tenant?.last_name} ${tenant?.first_name}`}
                                                        </Typography>
                                                    }
                                                    secondary={renderSubtext(
                                                        getOccupantLabel(tenant)
                                                    )}
                                                />
                                                <ListItemText
                                                    sx={{
                                                        maxWidth: '40%!important'
                                                    }}
                                                    primary={
                                                        <Typography
                                                            sx={{ fontSize: '13px' }}
                                                            component="span"
                                                            variant="body1"
                                                            color="text.primary"
                                                        >
                                                            <PatternFormat
                                                                value={
                                                                    tenant?.phone?.number
                                                                        ? tenant.phone
                                                                              .number
                                                                        : ''
                                                                }
                                                                displayType={'text'}
                                                                format={`+${tenant.phone?.code} (###)-###-####`}
                                                            />
                                                        </Typography>
                                                    }
                                                />
                                                {isMinor(tenant) && (
                                                    <ListItemText
                                                        sx={{
                                                            display: 'flex',
                                                            position: 'absolute',
                                                            right:
                                                                tenant.status ===
                                                                    'Invited' ||
                                                                tenant.status ===
                                                                    'Suspended'
                                                                    ? '19%'
                                                                    : '3%',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '12%'
                                                        }}
                                                        primary={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent:
                                                                        'center',
                                                                    alignItems: 'center',
                                                                    height: '80%',
                                                                    backgroundColor:
                                                                        'rgba(255, 0, 0, .1)',
                                                                    borderRadius: '5px'
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '13px',
                                                                        marginX: '10px'
                                                                    }}
                                                                    component="span"
                                                                    variant="body1"
                                                                    color="rgba(255, 0, 0, 1)"
                                                                >
                                                                    Minor
                                                                </Typography>
                                                            </div>
                                                        }
                                                    />
                                                )}
                                                {tenant.status !== 'Active' && (
                                                    <ListItemText
                                                        sx={{
                                                            display: 'flex',
                                                            position: 'absolute',
                                                            right: '3%',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '15%'
                                                        }}
                                                        primary={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent:
                                                                        'center',
                                                                    alignItems: 'center',
                                                                    height: '80%',

                                                                    backgroundColor:
                                                                        tenant.status ===
                                                                        'Active'
                                                                            ? 'rgba(45, 143, 87, .1)'
                                                                            : tenant.status ===
                                                                              'Invited'
                                                                            ? 'rgba(255, 192, 0, .1)'
                                                                            : 'rgba(255, 0, 0, .1)',
                                                                    borderRadius: '5px'
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '13px',
                                                                        marginX: '10px'
                                                                    }}
                                                                    component="span"
                                                                    variant="body1"
                                                                    color={
                                                                        tenant.status ===
                                                                        'Active'
                                                                            ? 'rgba(45, 143, 87, 1)'
                                                                            : tenant.status ===
                                                                              'Invited'
                                                                            ? 'rgba(255, 192, 0, 1)'
                                                                            : 'rgba(255, 0, 0, 1)'
                                                                    }
                                                                >
                                                                    {`${tenant?.status}`}
                                                                </Typography>
                                                            </div>
                                                        }
                                                    />
                                                )}
                                            </>
                                        </ListItem>
                                    ))}
                                    {(!filteredTenants ||
                                        filteredTenants.length === 0) && (
                                        <div
                                            style={{
                                                marginLeft: '3%',
                                                height: '4vh',
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontSize: '13px'
                                            }}
                                        >
                                            Tenants not found.
                                        </div>
                                    )}
                                </List>
                            </div>
                        </>
                    )}
                </Stack>
                <Stack
                    sx={{
                        display: 'contents'
                    }}
                >
                    <Stack
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'flex-start',
                            flexShrink: 0,
                            marginTop: '2vh',
                            alignItems: 'center',
                            height: '5.4vh',
                            width: '100%',
                            overflowY: 'auto',
                            border: '1px solid rgba(0, 0, 0, 0.06)'
                        }}
                    >
                        <Tabs
                            value={activeTab}
                            onChange={e => handleChange(e.target.id)}
                            indicatorColor="transparent"
                        >
                            <Tab
                                icon={
                                    <TabIcon
                                        number={guests?.length + visitors?.length || 0}
                                    />
                                }
                                iconPosition="end"
                                sx={{
                                    fontSize: '13px'
                                }}
                                label="Guests"
                                id="simple-tab-visit-guard-0"
                            />
                            <Tab
                                icon={<TabIcon number={accessLogs?.length || 0} />}
                                iconPosition="end"
                                sx={{
                                    fontSize: '13px'
                                }}
                                label="Access Logs"
                                id="simple-tab-accesslogs-1"
                            />
                            <Tab
                                icon={<TabIcon number={sortedCodes?.length || 0} />}
                                iconPosition="end"
                                sx={{
                                    fontSize: '13px'
                                }}
                                label="Codes"
                                id="simple-tab-memberscodes-2"
                            />
                        </Tabs>
                    </Stack>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '100%',
                            gridTemplateRows: 'minmax(0, 1fr)',
                            flex: '2 1 0',
                            minHeight: `max(calc(1.8vh + 31px + ${listFloor(
                                filteredGuests.length
                            )}), ${listFloor(accessLogs.length)}, ${listFloor(
                                sortedCodes.length
                            )})`,
                            maxHeight: 'max-content'
                        }}
                    >
                        <div
                            role="tabpanel"
                            aria-hidden={activeTab !== 0}
                            id={`simple-tabpanel-guests`}
                            style={{
                                gridArea: '1 / 1',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                visibility: activeTab === 0 ? 'visible' : 'hidden'
                            }}
                            aria-labelledby={`simple-tab-guests`}
                        >
                            <OutlinedInput
                                type="search"
                                placeholder="Search guests & visitors…"
                                value={guestSearch}
                                onChange={e => setGuestSearch(e.target.value)}
                                size="small"
                                inputProps={{
                                    sx: {
                                        fontSize: '13px',
                                        height: '1.8vh',
                                        backgroundColor: 'rgba(249, 249, 250, 1)'
                                    }
                                }}
                                sx={{ width: '100%', my: '6px', flexShrink: 0 }}
                            />
                            <List
                                sx={{
                                    width: '100%',
                                    overflowY: 'auto',
                                    flex: '0 1 auto',
                                    minHeight: 0,
                                    borderLeft: '1px solid rgba(243, 243, 243, 1)',
                                    borderRight: '1px solid rgba(243, 243, 243, 1)',
                                    borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                    padding: 0,
                                    margin: 0
                                }}
                            >
                                {filteredGuests.map(item =>
                                    item._type === 'guest' ? (
                                        <ListItem
                                            key={item.uid || item.invite_id}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                minHeight: '48px',
                                                paddingY: '6px',
                                                width: '100%',
                                                backgroundColor: '#ffffff',
                                                borderBottom:
                                                    '1px solid rgba(243, 243, 243, 1)',
                                                paddingLeft: '3%'
                                            }}
                                            button={true}
                                            disableGutters
                                            onClick={() => handleMemberDetails(item)}
                                        >
                                            <>
                                                <ListItemText
                                                    sx={{
                                                        maxWidth: '65%!important',
                                                        left: '4%',
                                                        my: 0,
                                                        paddingRight: '20px'
                                                    }}
                                                    primary={
                                                        <Typography
                                                            sx={{
                                                                fontSize: '13px',
                                                                textTransform:
                                                                    'capitalize',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: 'block'
                                                            }}
                                                            component="span"
                                                            variant="body1"
                                                            color="text.primary"
                                                            title={getGuestListDisplay(
                                                                item
                                                            )}
                                                        >
                                                            {getGuestListDisplay(item)}
                                                        </Typography>
                                                    }
                                                    secondary={renderSubtext(
                                                        getGuestAccessWindow(item)
                                                    )}
                                                />
                                                <ListItemText
                                                    sx={{ maxWidth: '15%!important' }}
                                                    primary={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'start',
                                                                alignItems: 'center',
                                                                height: '80%'
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    borderRadius: '5px',
                                                                    paddingX: 1.5
                                                                }}
                                                                component="span"
                                                                variant="body1"
                                                                color="success.main"
                                                                bgcolor="success.lighter"
                                                            >
                                                                {userRolesNames[
                                                                    getPropertyEntry(item)
                                                                        ?.role
                                                                ] || 'Guest'}
                                                            </Typography>
                                                        </div>
                                                    }
                                                />
                                                <ListItemText
                                                    sx={{
                                                        display: 'flex',
                                                        position: 'absolute',
                                                        right: '3%',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        width: '15%'
                                                    }}
                                                    primary={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                height: '80%',
                                                                backgroundColor:
                                                                    item.status ===
                                                                    'Active'
                                                                        ? 'rgba(45, 143, 87, .1)'
                                                                        : item.status ===
                                                                          'Invited'
                                                                        ? 'rgba(255, 192, 0, .1)'
                                                                        : 'rgba(255, 0, 0, .1)',
                                                                borderRadius: '5px'
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    marginX: '10px'
                                                                }}
                                                                component="span"
                                                                variant="body1"
                                                                color={
                                                                    item.status ===
                                                                    'Active'
                                                                        ? 'rgba(45, 143, 87, 1)'
                                                                        : item.status ===
                                                                          'Invited'
                                                                        ? 'rgba(255, 192, 0, 1)'
                                                                        : 'rgba(255, 0, 0, 1)'
                                                                }
                                                            >
                                                                {item.status}
                                                            </Typography>
                                                        </div>
                                                    }
                                                />
                                            </>
                                        </ListItem>
                                    ) : (
                                        <ListItem
                                            key={item.id}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-start',
                                                alignItems: 'center',
                                                minHeight: '48px',
                                                paddingY: '6px',
                                                width: '100%',
                                                backgroundColor: '#ffffff',
                                                borderBottom:
                                                    '1px solid rgba(243, 243, 243, 1)',
                                                paddingLeft: '3%'
                                            }}
                                            button={true}
                                            disableGutters
                                            onClick={() => handleLogCurrentVisitor(item)}
                                        >
                                            <>
                                                <ListItemText
                                                    sx={{
                                                        maxWidth: '65%!important',
                                                        left: '4%',
                                                        my: 0,
                                                        paddingRight: '20px'
                                                    }}
                                                    primary={
                                                        <Typography
                                                            sx={{
                                                                fontSize: '13px',
                                                                textTransform:
                                                                    'capitalize',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: 'block'
                                                            }}
                                                            component="span"
                                                            variant="body1"
                                                            color="text.primary"
                                                            title={GuestHelper.formatName(
                                                                item
                                                            )}
                                                        >
                                                            {GuestHelper.formatName(item)}
                                                        </Typography>
                                                    }
                                                    secondary={renderSubtext(
                                                        formatAccessWindow(item)
                                                    )}
                                                />
                                                <ListItemText
                                                    sx={{ maxWidth: '15%!important' }}
                                                    primary={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'start',
                                                                alignItems: 'center',
                                                                height: '80%'
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    borderRadius: '5px',
                                                                    paddingX: 1.5
                                                                }}
                                                                component="span"
                                                                variant="body1"
                                                                color="info.main"
                                                                bgcolor="info.lighter"
                                                            >
                                                                Visitor
                                                            </Typography>
                                                        </div>
                                                    }
                                                />
                                                <ListItemText
                                                    sx={{
                                                        display: 'flex',
                                                        position: 'absolute',
                                                        right: '3%',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        width: '15%'
                                                    }}
                                                    primary={
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                height: '80%',
                                                                backgroundColor:
                                                                    item.active === true
                                                                        ? 'rgba(45, 143, 87, .1)'
                                                                        : 'rgba(255, 0, 0, .1)',
                                                                borderRadius: '5px'
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    marginX: '10px'
                                                                }}
                                                                component="span"
                                                                variant="body1"
                                                                color={
                                                                    item.active === true
                                                                        ? 'rgba(45, 143, 87, 1)'
                                                                        : 'rgba(255, 0, 0, 1)'
                                                                }
                                                            >
                                                                {item.active === true
                                                                    ? 'Active'
                                                                    : 'Inactive'}
                                                            </Typography>
                                                        </div>
                                                    }
                                                />
                                            </>
                                        </ListItem>
                                    )
                                )}
                                {filteredGuests.length === 0 && (
                                    <div
                                        style={{
                                            marginLeft: '3%',
                                            height: '4vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '13px'
                                        }}
                                    >
                                        Guests not found.
                                    </div>
                                )}
                            </List>
                        </div>
                        <div
                            role="tabpanel"
                            aria-hidden={activeTab !== 1}
                            id={`simple-tabpanel-accesslogs`}
                            style={{
                                gridArea: '1 / 1',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                visibility: activeTab === 1 ? 'visible' : 'hidden'
                            }}
                            aria-labelledby={`simple-tab-accesslogs`}
                        >
                            <List
                                sx={{
                                    width: '100%',
                                    overflowY: 'auto',
                                    flex: '0 1 auto',
                                    minHeight: 0,
                                    borderLeft: '1px solid rgba(243, 243, 243, 1)',
                                    borderRight: '1px solid rgba(243, 243, 243, 1)',
                                    borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                    padding: 0,
                                    margin: 0
                                }}
                            >
                                {accessLogs && accessLogs.length !== 0 ? (
                                    <>
                                        {accessLogs.map(accessLog => (
                                            <ListItem
                                                key={accessLog.id}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    paddingY: 1,
                                                    paddingX: 2,
                                                    backgroundColor: '#fff',
                                                    borderBottom:
                                                        '1px solid rgba(243, 243, 243, 1)',
                                                    width: '100%'
                                                }}
                                                disableGutters
                                            >
                                                <Box sx={{ width: '60%' }}>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '13px'
                                                        }}
                                                        color="text.primary"
                                                    >
                                                        {accessLog?.str}
                                                    </Typography>
                                                    <Typography
                                                        sx={{ fontSize: '12px' }}
                                                        color="text.secondary"
                                                    >
                                                        {AccessLogHelper.formatAccessTime(
                                                            accessLog.access_time
                                                        )}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: '20%',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{ fontSize: '13px' }}
                                                        color="text.primary"
                                                    >
                                                        {userRolesNames[accessLog.role]}
                                                    </Typography>
                                                    <Typography
                                                        sx={{ fontSize: '12px' }}
                                                        color="text.secondary"
                                                    >
                                                        {getLogType(accessLog)}
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        width: '20%',
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            backgroundColor:
                                                                accessLog.success
                                                                    ? 'rgba(45, 143, 87, 0.1)'
                                                                    : 'rgba(255, 0, 0, 0.1)',
                                                            color: accessLog.success
                                                                ? 'rgba(45, 143, 87, 1)'
                                                                : 'rgba(255, 0, 0, 1)',
                                                            borderRadius: '5px',
                                                            paddingX: '10px',
                                                            fontSize: '13px',
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {accessLog.success
                                                            ? 'Accessed'
                                                            : 'Denied'}
                                                    </Box>
                                                </Box>
                                            </ListItem>
                                        ))}
                                        {accessLogsHasMore && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    paddingY: 1
                                                }}
                                            >
                                                <Button
                                                    onClick={handleLoadMoreAccessLogs}
                                                    disabled={accessLogsLoadingMore}
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        textTransform: 'none',
                                                        minWidth: 'auto',
                                                        paddingY: '4px',
                                                        paddingX: '16px',
                                                        borderRadius: '999px'
                                                    }}
                                                >
                                                    {accessLogsLoadingMore
                                                        ? 'Loading...'
                                                        : 'Load more'}
                                                </Button>
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <div
                                        style={{
                                            marginLeft: '3%',
                                            height: '4vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '13px'
                                        }}
                                    >
                                        Access Logs not found.
                                    </div>
                                )}
                            </List>
                        </div>
                        <div
                            role="tabpanel"
                            aria-hidden={activeTab !== 2}
                            id={`simple-tabpanel-memberscodes`}
                            style={{
                                gridArea: '1 / 1',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                visibility: activeTab === 2 ? 'visible' : 'hidden'
                            }}
                            aria-labelledby={`simple-tab-memberscodes`}
                        >
                            <List
                                sx={{
                                    width: '100%',
                                    overflowY: 'auto',
                                    flex: '0 1 auto',
                                    minHeight: 0,
                                    borderLeft: '1px solid rgba(243, 243, 243, 1)',
                                    borderRight: '1px solid rgba(243, 243, 243, 1)',
                                    borderBottom: '1px solid rgba(243, 243, 243, 1)',
                                    padding: 0,
                                    margin: 0
                                }}
                            >
                                {sortedCodes && sortedCodes.length !== 0 ? (
                                    sortedCodes.map(code => (
                                        <React.Fragment key={code.code_id}>
                                            {code &&
                                            code.created_at &&
                                            code.expires_at ? (
                                                <ListItem
                                                    key={code.code_id}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-start',
                                                        alignItems: 'center',
                                                        minHeight: '48px',
                                                        paddingY: '6px',
                                                        width: '100%',
                                                        backgroundColor: '#ffffff',
                                                        borderBottom:
                                                            '1px solid rgba(243, 243, 243, 1)',
                                                        paddingLeft: '3%'
                                                    }}
                                                    button={true}
                                                    disableGutters
                                                    onClick={() => {
                                                        handleOpenOtcModal(code);
                                                    }}
                                                >
                                                    <ListItemText
                                                        sx={{
                                                            maxWidth: '65%!important',
                                                            left: '4%',
                                                            my: 0,
                                                            paddingRight: '20px'
                                                        }}
                                                        primary={
                                                            <Typography
                                                                sx={{
                                                                    fontSize: '13px',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow:
                                                                        'ellipsis',
                                                                    display: 'block'
                                                                }}
                                                                component="span"
                                                                variant="body1"
                                                                color="text.primary"
                                                                title={`${getCodeOwner(
                                                                    code
                                                                )} - ${code?.name}`}
                                                            >
                                                                {`${getCodeOwner(
                                                                    code
                                                                )} - ${code?.name}`}
                                                            </Typography>
                                                        }
                                                        secondary={renderSubtext(
                                                            formatCodeWindow(code)
                                                        )}
                                                    />
                                                    <ListItemText
                                                        sx={{ maxWidth: '15%!important' }}
                                                        primary={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent:
                                                                        'start',
                                                                    alignItems: 'center',
                                                                    height: '80%'
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '13px',
                                                                        fontWeight: 500,
                                                                        letterSpacing:
                                                                            '0.08em',
                                                                        fontVariantNumeric:
                                                                            'tabular-nums',
                                                                        borderRadius:
                                                                            '5px',
                                                                        paddingX: 1.5,
                                                                        backgroundColor:
                                                                            'rgba(243, 243, 243, 1)'
                                                                    }}
                                                                    component="span"
                                                                    variant="body1"
                                                                    color="text.primary"
                                                                >
                                                                    {code?.code}
                                                                </Typography>
                                                            </div>
                                                        }
                                                    />
                                                    <ListItemText
                                                        sx={{
                                                            display: 'flex',
                                                            position: 'absolute',
                                                            right: '3%',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            width: '15%'
                                                        }}
                                                        primary={
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    justifyContent:
                                                                        'center',
                                                                    alignItems: 'center',
                                                                    height: '80%',
                                                                    backgroundColor:
                                                                        isActive(code)
                                                                            ? 'rgba(45, 143, 87, .1)'
                                                                            : 'rgba(255, 0, 0, .1)',
                                                                    borderRadius: '5px'
                                                                }}
                                                            >
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: '13px',
                                                                        marginX: '10px'
                                                                    }}
                                                                    component="span"
                                                                    variant="body1"
                                                                    color={
                                                                        isActive(code)
                                                                            ? 'rgba(45, 143, 87, 1)'
                                                                            : 'rgba(255, 0, 0, 1)'
                                                                    }
                                                                >
                                                                    {isActive(code)
                                                                        ? 'Active'
                                                                        : 'Inactive'}
                                                                </Typography>
                                                            </div>
                                                        }
                                                    />
                                                </ListItem>
                                            ) : null}
                                        </React.Fragment>
                                    ))
                                ) : (
                                    <div
                                        style={{
                                            marginLeft: '3%',
                                            height: '4vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '13px'
                                        }}
                                    >
                                        Access Codes not found.
                                    </div>
                                )}
                            </List>
                        </div>
                    </Box>
                </Stack>
                <Stack
                    sx={{
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        position: 'sticky',
                        bottom: 0,
                        marginTop: 'auto',
                        flexShrink: 0,
                        minHeight: '11vh',
                        backgroundColor: '#ffffff',
                        paddingY: '1.5vh',
                        zIndex: 1
                    }}
                >
                    <Button
                        sx={{
                            position: 'absolute',
                            bottom: '1vh',
                            width: '40%',
                            height: '4.5vh',
                            borderRadius: '26.5px',
                            '&:active::after': {
                                borderRadius: '26.5px'
                            },
                            fontSize: '0.9375rem',
                            fontWeight: '600'
                        }}
                        onClick={handleLogNewVisitor}
                        variant="contained"
                    >
                        Log New Visitor
                    </Button>
                </Stack>
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    keepMounted
                    fullWidth
                    onClose={() => setOpenPropertyNotesModal(false)}
                    open={openPropertyNotesModal}
                    sx={{
                        '& .MuiDialog-paper': { p: 0 },
                        transition: 'transform 225ms'
                    }}
                    aria-describedby="alert-dialog-slide-description"
                >
                    {openPropertyNotesModal && (
                        <PropertyNotesModal
                            onClose={() => setOpenPropertyNotesModal(false)}
                            propertyNotes={propertyNotes}
                        />
                    )}
                </Dialog>
            </Stack>
        </>
    );
};

export default PropertyDetails;
